import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { DateUtils } from 'common';
import { Calendar } from 'src/components';

@Component({
  tag: 'date-selector',
})
export class DateSelector {
  static async presentModal(date: string, callback: (date: string) => void): Promise<void> {
    const modal: any = await modalController.create({
      component: 'ion-datetime',
      componentProps: { 'presentation': 'date', 'show-default-buttons': 'true', 'value': date },
      cssClass: 'date-picker-modal',
      showBackdrop: true,
    });

    modal.addEventListener('ionChange', async (e: CustomEvent) => {
      callback(e.detail.value);
      modal.dismiss();
    });

    modal.present();
  }

  static async present(modalTitle: string, date: string, maxDate: string = null, calendar: Calendar = null): Promise<string> {
    return new Promise(async resolve => {
      const modal = await modalController.create({
        component: 'date-selector',
        componentProps: { modalTitle, date, maxDate, calendar },
        cssClass: ['concourse-modal', 'modal-height-auto'],
        backdropDismiss: false,
      });
      modal.addEventListener('selected', (e: CustomEvent) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  static async presentClearable(modalTitle: string, date: string, maxDate: string = null, calendar: Calendar = null): Promise<string | boolean> {
    return new Promise(async resolve => {
      const modal = await modalController.create({
        component: 'date-selector',
        componentProps: { modalTitle, date, maxDate, clearDate: true, calendar },
        cssClass: ['concourse-modal', 'modal-height-auto'],
        backdropDismiss: false,
      });
      modal.addEventListener('selected', (e: CustomEvent) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  static async presentModal2(date: Date, callback: (date: Date) => void): Promise<void> {
    const modal: any = await modalController.create({
      component: 'ion-datetime',
      componentProps: { 'presentation': 'date', 'show-default-buttons': 'true', 'value': date.toISOString() },
      cssClass: 'date-picker-modal',
      showBackdrop: true,
    });

    modal.addEventListener('ionChange', async (e: CustomEvent) => {
      callback(new Date(e.detail.value));
      modal.dismiss();
    });

    modal.present();
  }

  @Prop() modalTitle: string;
  @Prop() date: string;
  @Prop() maxDate: string | null;
  @Prop() calendar: Calendar;
  @Prop() clearDate: boolean = false;

  @State() selectedDate: string;

  @Event() selected: EventEmitter<string | boolean>;
  @Event() dismiss: EventEmitter<void>;

  render() {
    const maxDate = this.maxDate ?? this.adjustYearsAndGetISOString(30);

    return (
      <Fragment>
        <modal-header modalTitle={this.modalTitle || 'Select Date'} onDismiss={() => this.dismiss.emit()}></modal-header>
        <ion-datetime 
          presentation="date" 
          show-default-buttons="false" 
          value={this.date} 
          max={maxDate} 
          isDateEnabled={(date: string) => this.isDateEnabled(date)} 
          onIonChange={e => (this.selectedDate = e.detail.value)} 
        />
        <ion-footer class="ion-padding-horizontal">
          <ion-toolbar>
            {this.clearDate && (
              <ion-button slot="start" fill="outline" onClick={() => this.selected.emit(false)}>
                Clear Date
              </ion-button>
            )}
            <ion-button slot="end" color="primary" onClick={() => this.selected.emit(this.selectedDate)} disabled={!this.selectedDate}>
              Select {DateUtils.formatIso(this.selectedDate)}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Fragment>
    );
  }

  private adjustYearsAndGetISOString(years: number): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() + years);
    date.setMonth(11);
    return date.toISOString();
  }

  private isDateEnabled(date: string): boolean {
    if (!this.calendar) return true;
    return this.calendar.isWorkingDate(DateUtils.fromISO(date));
  }
}
